<template>
    <app-modal-wrapper>
        <div class="modal-meter-extended">
            <div class="modal-meter-extended__content">
                <h4>{{ title }}</h4>

                <div v-if="!hasData" class="modal-meter-extended__empty">
                    {{ $t('electricity.extended.noData') }}
                </div>

                <template v-else>
                    <div class="modal-meter-extended__summary">
                        <div class="meter-extended-cell">
                            <span class="meter-extended-cell__label">{{ $t('electricity.extended.summary.readings') }}</span>
                            <span class="meter-extended-cell__value">{{ fmt(extended.readings, 4, units.energy) }}</span>
                        </div>
                        <div class="meter-extended-cell">
                            <span class="meter-extended-cell__label">{{ $t('electricity.extended.summary.consumption') }}</span>
                            <span class="meter-extended-cell__value">{{ fmt(extended.consumption, 2, units.power) }}</span>
                        </div>
                        <div class="meter-extended-cell">
                            <span class="meter-extended-cell__label">{{ $t('electricity.extended.summary.frequency') }}</span>
                            <span class="meter-extended-cell__value">{{ fmt(extended.frequency) }}</span>
                        </div>
                        <div class="meter-extended-cell">
                            <span class="meter-extended-cell__label">{{ $t('electricity.extended.summary.cos') }}</span>
                            <span class="meter-extended-cell__value">{{ fmt(extended.cos) }}</span>
                        </div>
                    </div>

                    <div class="modal-meter-extended__section">
                        <h5>{{ $t('electricity.extended.tariffs.title') }}</h5>
                        <table class="meter-extended-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('electricity.extended.tariffs.tariff') }}</th>
                                    <th>{{ $t('electricity.extended.tariffs.readings') }}</th>
                                    <th>{{ $t('electricity.extended.tariffs.active_negative') }}</th>
                                    <th>{{ $t('electricity.extended.tariffs.reactive_positive') }}</th>
                                    <th>{{ $t('electricity.extended.tariffs.reactive_negative') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="t in tariffKeys" :key="t">
                                    <td>{{ $t('electricity.extended.tariffs.' + t) }}</td>
                                    <td>{{ fmt(extended.tariffs?.[t]?.readings, 4, units.energy) }}</td>
                                    <td>{{ fmt(extended.tariffs?.[t]?.active_negative, 4, units.energy) }}</td>
                                    <td>{{ fmt(extended.tariffs?.[t]?.reactive_positive, 4, units.reactiveEnergy) }}</td>
                                    <td>{{ fmt(extended.tariffs?.[t]?.reactive_negative, 4, units.reactiveEnergy) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="modal-meter-extended__section">
                        <h5>{{ $t('electricity.extended.phases.title') }}</h5>
                        <table class="meter-extended-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('electricity.extended.phases.parameter') }}</th>
                                    <th>{{ $t('electricity.extended.phases.a') }}</th>
                                    <th>{{ $t('electricity.extended.phases.b') }}</th>
                                    <th>{{ $t('electricity.extended.phases.c') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="p in phaseRows" :key="p.key">
                                    <td>{{ $t('electricity.extended.phases.' + p.key) }}</td>
                                    <td>{{ fmt(extended.phases?.[p.key]?.a, p.decimals, p.unit) }}</td>
                                    <td>{{ fmt(extended.phases?.[p.key]?.b, p.decimals, p.unit) }}</td>
                                    <td>{{ fmt(extended.phases?.[p.key]?.c, p.decimals, p.unit) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="modal-meter-extended__section">
                        <h5>{{ $t('electricity.extended.energy.title') }}</h5>
                        <div class="modal-meter-extended__summary">
                            <div class="meter-extended-cell">
                                <span class="meter-extended-cell__label">{{ $t('electricity.extended.energy.active_negative') }}</span>
                                <span class="meter-extended-cell__value">{{ fmt(extended.energy?.active_negative, 4, units.energy) }}</span>
                            </div>
                            <div class="meter-extended-cell">
                                <span class="meter-extended-cell__label">{{ $t('electricity.extended.energy.reactive_positive') }}</span>
                                <span class="meter-extended-cell__value">{{ fmt(extended.energy?.reactive_positive, 4, units.reactiveEnergy) }}</span>
                            </div>
                            <div class="meter-extended-cell">
                                <span class="meter-extended-cell__label">{{ $t('electricity.extended.energy.reactive_negative') }}</span>
                                <span class="meter-extended-cell__value">{{ fmt(extended.energy?.reactive_negative, 4, units.reactiveEnergy) }}</span>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <div class="modal-meter-extended__control">
                <app-base-btn
                    :variation="'cancel'"
                    :text="$t('common.buttons.cancel')"
                    @action="closeModalMeterExtended"
                />
            </div>
        </div>
    </app-modal-wrapper>
</template>

<script>
    import { greenHubAPI } from "../../../config/api";

    const REFRESH_INTERVAL_MS = 5000;

    export default {
        name: "ModalElectricityMeterExtended",

        props: {
            selectedMeter: {
                type: Object,
                required: true
            }
        },

        data() {
            return {
                // Live readout, refreshed by polling while the modal is open.
                // Seeded from the value captured when the modal was opened.
                liveExtended: this.selectedMeter?.extended || null,
                pollId: null,
                tariffKeys: ['t1', 't2', 't3', 't4']
            }
        },

        mounted() {
            // Periodic GET so the numbers update live while the modal stays open.
            if (this.selectedMeter?.buildingId && this.selectedMeter?.id) {
                this.pollId = setInterval(this.refreshExtended, REFRESH_INTERVAL_MS);
            }
        },

        beforeUnmount() {
            this.stopPolling();
        },

        computed: {
            extended() {
                // Current readout from RAM (GET); null when device sent only the minimal set.
                return this.liveExtended || {};
            },

            hasData() {
                return !!this.liveExtended;
            },

            units() {
                return {
                    energy: this.$t('common.units.energy'),                 // кВт·год (active energy)
                    reactiveEnergy: this.$t('common.units.reactiveEnergy'), // кВАр·год (reactive energy)
                    power: this.$t('common.units.power'),                   // кВт (active power)
                    reactivePower: this.$t('common.units.reactivePower'),   // кВАр (reactive power)
                    current: this.$t('common.units.current'),
                    voltage: this.$t('common.units.voltage')
                };
            },

            // Per-phase rows: active power (кВт), reactive power (кВАр), current (А), voltage (В).
            phaseRows() {
                return [
                    { key: 'cos', decimals: 2, unit: '' },
                    { key: 'current', decimals: 2, unit: this.units.current },
                    { key: 'voltage', decimals: 2, unit: this.units.voltage },
                    { key: 'consumption', decimals: 2, unit: this.units.power },
                    { key: 'q_positive', decimals: 2, unit: this.units.reactivePower },
                    { key: 'q_negative', decimals: 2, unit: this.units.reactivePower }
                ];
            },

            title() {
                const name = this.selectedMeter?.premiseName || this.selectedMeter?.hwId || '';
                return name
                    ? `${this.$t('electricity.extended.title')} — ${name}`
                    : this.$t('electricity.extended.title');
            }
        },

        methods: {
            fmt(value, decimals = 2, unit = '') {
                if (value === undefined || value === null || value === '' || isNaN(Number(value))) {
                    return '—';
                }
                const num = Number(value).toFixed(decimals);
                return unit ? `${num} ${unit}` : num;
            },

            refreshExtended() {
                const buildingId = this.selectedMeter.buildingId;
                const meterId = String(this.selectedMeter.id);

                greenHubAPI.get(`electricity/${buildingId}`)
                    .then((result) => {
                        const found = this.findExtendedById(result.data || [], meterId);
                        // Update only on a resolved response; keep last value if request failed.
                        this.liveExtended = found;
                    })
                    .catch(() => {});
            },

            findExtendedById(switchgears, meterId) {
                for (const sg of switchgears) {
                    for (const out of (sg.outputMeters || [])) {
                        if (String(out._id) === meterId) return out.extended || null;
                        for (const consumer of (out.consumerMeters || [])) {
                            if (String(consumer._id) === meterId) return consumer.extended || null;
                        }
                    }
                    for (const inp of (sg.inputMeters || [])) {
                        if (String(inp._id) === meterId) return inp.extended || null;
                    }
                }
                return null;
            },

            stopPolling() {
                if (this.pollId) {
                    clearInterval(this.pollId);
                    this.pollId = null;
                }
            },

            closeModalMeterExtended() {
                this.stopPolling();
                this.closeModal('modalElectricityMeterExtended');
            }
        }
    }
</script>

<style lang="scss" scoped>
    .modal-meter-extended {
        background: var(--system-white);
        padding: 30px;
        display: flex;
        flex-direction: column;
        max-width: 1200px;
        width: 100%;
        max-height: 88vh;
        overflow-y: auto;

        h4 {
            margin-bottom: 20px;
        }

        h5 {
            margin-bottom: 12px;
        }

        &__empty {
            padding: 30px 0;
            text-align: center;
            color: var(--app-base-secondary);
        }

        &__summary {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            margin-bottom: 24px;
        }

        &__section {
            margin-bottom: 24px;
        }

        &__control {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
        }
    }

    .meter-extended-cell {
        display: flex;
        flex-direction: column;

        &__label {
            font-size: 12px;
            color: var(--app-base-secondary);
        }

        &__value {
            font-size: 14px;
        }
    }

    .meter-extended-table {
        width: 100%;
        border-collapse: collapse;

        th, td {
            padding: 8px 12px;
            text-align: left;
            border-bottom: 1px solid var(--app-border);
            font-size: 13px;
        }

        th {
            color: var(--app-base-secondary);
            font-weight: 500;
        }
    }
</style>
